import { useEffect, useState } from "react";
import api from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";
import { Wallet, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";

const fmt = (n) => "₹" + Number(n||0).toLocaleString("en-IN",{maximumFractionDigits:0});
const COLORS = ["#f97316","#0ea5e9","#10b981","#8b5cf6","#f59e0b","#ec4899","#14b8a6","#eab308"];

export default function FinancingTab({ pid }) {
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/projects/${pid}/financing`).then(r => setData(r.data)); }, [pid]);

  if (!data) return <div className="p-6 text-sm text-muted-foreground">Loading financing…</div>;

  const isOverbudget = data.actual > data.approved && data.approved > 0;
  const barData = [
    { name: "Budget", value: data.budget },
    { name: "Estimated", value: data.estimated },
    { name: "Approved", value: data.approved },
    { name: "Actual", value: data.actual },
    { name: "Billed", value: data.billed_to_client },
    { name: "Collected", value: data.collected },
  ];

  return (
    <div className="space-y-4" data-testid="financing-tab">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard label="Budget" value={fmt(data.budget)} icon={Wallet} />
        <KPICard label="Estimated (BOQ)" value={fmt(data.estimated)} />
        <KPICard label="Approved" value={fmt(data.approved)} />
        <KPICard label="Actual Cost" value={fmt(data.actual)} highlight={isOverbudget} sub={`Util ${data.budget_utilisation}%`} />
        <KPICard label="Variance" value={fmt(data.variance)} tone={data.variance>=0 ? "good" : "bad"} icon={data.variance>=0 ? TrendingUp : TrendingDown} sub={`${data.variance_percent}%`} testid="variance-kpi" />
        <KPICard label="Billed to Client" value={fmt(data.billed_to_client)} />
        <KPICard label="Collected" value={fmt(data.collected)} tone="good" />
        <KPICard label="Outstanding" value={fmt(data.outstanding)} tone={data.outstanding>0 ? "warn" : "muted"} />
      </div>

      {isOverbudget && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm flex items-center gap-2" data-testid="overbudget-warning">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <span className="text-amber-900 dark:text-amber-100">Actual spend ({fmt(data.actual)}) has exceeded the approved cost ({fmt(data.approved)}) by {fmt(data.actual - data.approved)}. Review variance.</span>
        </div>
      )}

      {/* P&L Summary */}
      <div className="rounded-2xl bg-card border border-border/60 p-5" data-testid="pnl-summary">
        <h3 className="font-display text-lg font-semibold mb-3">Profit & Loss</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-[10px] uppercase text-muted-foreground">Revenue (Collected)</div>
            <div className="font-display text-xl">{fmt(data.collected)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-muted-foreground">Cost (Actual)</div>
            <div className="font-display text-xl">{fmt(data.actual)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-muted-foreground">Profit</div>
            <div className={`font-display text-xl ${data.profit>=0 ? "text-emerald-600" : "text-rose-600"}`}>{fmt(data.profit)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-muted-foreground">Margin</div>
            <div className={`font-display text-xl ${data.profit>=0 ? "text-emerald-600" : "text-rose-600"}`}>{data.profit_margin}%</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Budget vs Spend */}
        <div className="rounded-2xl bg-card border border-border/60 p-5">
          <h3 className="font-display text-base font-semibold mb-3">Budget vs Spend</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={barData}>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v>=100000 ? `${(v/100000).toFixed(1)}L` : v} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Bar dataKey="value" fill="#f97316" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Expense by category */}
        <div className="rounded-2xl bg-card border border-border/60 p-5">
          <h3 className="font-display text-base font-semibold mb-3">Actual Cost Breakdown</h3>
          {data.actual_breakdown.expenses + data.actual_breakdown.contractor_paid + data.actual_breakdown.material_cost === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">No costs recorded yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={[
                  { name: "Expenses", value: data.actual_breakdown.expenses },
                  { name: "Contractor Payments", value: data.actual_breakdown.contractor_paid },
                  { name: "Materials", value: data.actual_breakdown.material_cost },
                ].filter(x => x.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => e.name}>
                  {COLORS.map((c,i) => <Cell key={i} fill={c} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Expense categories table */}
      {data.expense_by_category?.length > 0 && (
        <div className="rounded-2xl bg-card border border-border/60 p-5">
          <h3 className="font-display text-base font-semibold mb-3">Expenses by Category</h3>
          <div className="space-y-2">
            {data.expense_by_category.map((c,i) => (
              <div key={c.category} data-testid={`expense-cat-${c.category}`} className="grid grid-cols-6 items-center gap-3 text-sm">
                <div className="col-span-2 font-medium">{c.category}</div>
                <div className="col-span-3 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full" style={{ width: `${(c.amount / data.actual_breakdown.expenses) * 100}%`, background: COLORS[i%COLORS.length] }} /></div>
                <div className="text-right font-medium">{fmt(c.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* BOQs + Invoices */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-card border border-border/60 overflow-hidden">
          <div className="p-4 border-b border-border/50"><h3 className="font-display text-base font-semibold">Quotations & BOQs</h3></div>
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground"><tr><th className="text-left px-4 py-2">Number</th><th className="text-left">Title</th><th className="text-right">Total</th><th>Status</th></tr></thead>
            <tbody>{data.boqs.map(b => <tr key={b.id} data-testid={`fin-boq-${b.id}`} className="border-t border-border/50"><td className="px-4 py-2 font-mono text-xs">{b.number}</td><td>{b.title}</td><td className="text-right">{fmt(b.total)}</td><td className="text-[10px] uppercase text-muted-foreground text-center">{b.status}</td></tr>)}</tbody>
          </table>
          {!data.boqs.length && <div className="p-6 text-center text-sm text-muted-foreground">No BOQs yet.</div>}
        </div>
        <div className="rounded-2xl bg-card border border-border/60 overflow-hidden">
          <div className="p-4 border-b border-border/50"><h3 className="font-display text-base font-semibold">Client Invoices</h3></div>
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground"><tr><th className="text-left px-4 py-2">Number</th><th className="text-right">Total</th><th>Status</th></tr></thead>
            <tbody>{data.invoices.map(i => <tr key={i.id} data-testid={`fin-inv-${i.id}`} className="border-t border-border/50"><td className="px-4 py-2 font-mono text-xs">{i.number}</td><td className="text-right">{fmt(i.total)}</td><td className="text-[10px] uppercase text-muted-foreground text-center">{i.status}</td></tr>)}</tbody>
          </table>
          {!data.invoices.length && <div className="p-6 text-center text-sm text-muted-foreground">No invoices yet.</div>}
        </div>
      </div>
    </div>
  );
}

function KPICard({ label, value, sub, tone="default", icon: Icon, highlight, testid }) {
  const cls = tone === "good" ? "text-emerald-600" : tone === "bad" ? "text-rose-600" : tone === "warn" ? "text-amber-600" : tone === "muted" ? "text-muted-foreground" : "";
  return (
    <div data-testid={testid} className={`rounded-2xl border p-4 ${highlight ? "bg-amber-50 border-amber-300 dark:bg-amber-950/30" : "bg-card border-border/60"}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
        {Icon && <Icon className={`w-3.5 h-3.5 ${cls || "text-muted-foreground"}`} />}
      </div>
      <div className={`font-display text-xl font-semibold mt-0.5 ${cls}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
